import type { IObservableArray } from 'mobx';
import { runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Targeting, TTDCampaign } from '@feathr/blackbox';
import { EBiddingStrategyClass, ECampaignObjective } from '@feathr/blackbox';
import { Select } from '@feathr/components';
import { useObjectiveUtils } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';
import { useAccount, useFlags } from '@feathr/report_components';

import { validateStepOptimize } from '../AdWizardOptimizationStep.utils';
import ObjectiveChangeConfirmationModal from './ObjectiveChangeConfirmationModal';
import type { IObjectiveOption } from './ObjectiveOption';
import ObjectiveOption from './ObjectiveOption';
import ObjectiveValue from './ObjectiveValue';

interface IObjectiveSelectProps {
  campaign: TTDCampaign;
  targetings: IObservableArray<Targeting>;
  onNavigateToTargetsStep?: () => void;
}

function ObjectiveSelect({
  campaign,
  targetings,
  onNavigateToTargetsStep,
}: Readonly<IObjectiveSelectProps>): JSX.Element {
  const { t } = useTranslation();
  const { hasBuildReachAwarenessPP } = useFlags();
  const { isPlatformLite } = useAccount();
  const { Targetings } = useStore();
  const { createSelectOptions } = useObjectiveUtils();
  const options = createSelectOptions(
    campaign.getAvailableObjectives({ hasBuildReachAwarenessPP, isPlatformLite }),
  );
  const selectedOption = options.find((option) => option.id === campaign.get('objective'));
  const [touched, setTouched] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [pendingObjective, setPendingObjective] = useState<ECampaignObjective | null>(null);

  const validationErrors = validateStepOptimize(campaign);
  const error = flattenError(validationErrors.objective);
  const isDisabled = campaign.isPastEndDate && !campaign.isDraft;

  function checkDisabled(option: IObjectiveOption): boolean {
    return Boolean(option.disabled);
  }

  const shouldShowConfirmationModal = useCallback(
    (currentObjective: ECampaignObjective, newObjective: ECampaignObjective): boolean => {
      // Only show modal if feature flag is enabled and campaign is Pixl Plus (Omni)
      if (!hasBuildReachAwarenessPP || !campaign.isOmniCampaign) {
        return false;
      }

      const isReachObjective = (objective: ECampaignObjective): boolean =>
        objective === ECampaignObjective.Reach;

      // Show modal when changing FROM Build Reach and Awareness to other objectives
      if (isReachObjective(currentObjective) && !isReachObjective(newObjective)) {
        return true;
      }

      // Show modal when changing TO Build Reach and Awareness from other objectives
      if (!isReachObjective(currentObjective) && isReachObjective(newObjective)) {
        return true;
      }

      return false;
    },
    [hasBuildReachAwarenessPP, campaign.isOmniCampaign],
  );

  const clearAllTargets = useCallback(() => {
    // Collect ephemeral targets first to avoid skipping items during iteration
    const ephemeralTargets = targetings.filter((targeting) => targeting.isEphemeral);

    runInAction(() => {
      // Remove all ephemeral targets
      ephemeralTargets.forEach((targeting) => {
        targetings.remove(targeting);
        Targetings.remove(targeting.id);
      });

      // Archive all non-ephemeral targets
      targetings.forEach((targeting) => {
        if (!targeting.isEphemeral) {
          targeting.set({ is_archived: true });
        }
      });
    });
  }, [targetings, Targetings]);

  const setObjective = useCallback(
    (objective: ECampaignObjective) => {
      campaign.set({
        objective,
      });

      // Force the optimization strategy to auto when an auto-only objective is selected
      if (objective !== ECampaignObjective.Custom) {
        const currentBiddingStrategy = toJS(campaign.get('bidding_strategy'));

        campaign.set({
          bidding_strategy: {
            ...currentBiddingStrategy,
            _cls: EBiddingStrategyClass.Auto,
          },
        });
      }
    },
    [campaign],
  );

  function handleSelectSingle(option: IObjectiveOption): void {
    const currentObjective = campaign.get('objective');
    const newObjective = option.id as ECampaignObjective;

    if (shouldShowConfirmationModal(currentObjective, newObjective)) {
      setPendingObjective(newObjective);
      setShowConfirmModal(true);
    } else {
      setObjective(newObjective);
    }
  }

  function handleConfirmObjectiveChange(): void {
    if (pendingObjective) {
      clearAllTargets();
      setObjective(pendingObjective);

      // Navigate to targets step (Step 2 in the wizard)
      if (onNavigateToTargetsStep) {
        onNavigateToTargetsStep();
      }
    }

    setShowConfirmModal(false);
    setPendingObjective(null);
  }

  function handleCancelObjectiveChange(): void {
    setShowConfirmModal(false);
    setPendingObjective(null);
  }

  function handleBlur(): void {
    setTouched(true);
  }

  return (
    <>
      <Select
        attribute={'objective'}
        components={{
          Option: ObjectiveOption,
          SingleValue: ObjectiveValue,
        }}
        disabled={isDisabled}
        isOptionDisabled={checkDisabled}
        label={t('Campaign objective')}
        name={'campaign_objective'}
        onBlur={handleBlur}
        onSelectSingle={handleSelectSingle}
        options={options}
        placeholder={t('Select an objective...')}
        // TODO: move touched behavior to component in library
        validationError={touched ? error : undefined}
        value={selectedOption as IObjectiveOption}
      />
      {showConfirmModal && pendingObjective && (
        <ObjectiveChangeConfirmationModal
          currentObjective={campaign.get('objective')}
          newObjective={pendingObjective}
          onClose={handleCancelObjectiveChange}
          onConfirm={handleConfirmObjectiveChange}
          opened={showConfirmModal}
        />
      )}
    </>
  );
}

export default observer(ObjectiveSelect);
